import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../services/api';
import { toast } from 'sonner';

const API_URL = (typeof window !== 'undefined' && window.location.hostname === 'app.salesqualifyd.com')
  ? 'https://app.salesqualifyd.com' : (process.env.REACT_APP_BACKEND_URL || '');

export default function OAuthCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('Processing...');
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const code = searchParams.get('code');
    const state = searchParams.get('state');
    const error = searchParams.get('error');
    const errorDescription = searchParams.get('error_description');
    
    // Determine which platform based on URL path
    const path = window.location.pathname;
    
    if (error) {
      setStatus(`Authorization failed: ${errorDescription || error}`);
      toast.error(`OAuth failed: ${errorDescription || error}`);
      setTimeout(() => navigate('/automation/social'), 2000);
      return;
    }

    if (!code) {
      setStatus('No authorization code received');
      toast.error('No authorization code received');
      setTimeout(() => navigate('/automation/social'), 2000);
      return;
    }

    const handleCallback = async () => {
      try {
        if (path.includes('linkedin')) {
          setStatus('Connecting LinkedIn...');
          const clientId = localStorage.getItem('linkedin_client_id');
          const clientSecret = localStorage.getItem('linkedin_client_secret');
          const organizationId = localStorage.getItem('linkedin_organization_id');
          const redirectUri = localStorage.getItem('linkedin_redirect_uri') || `${window.location.origin}/auth/linkedin/callback`;
          
          if (!clientId || !clientSecret) {
            throw new Error('LinkedIn credentials not found. Please try connecting again.');
          }

          const response = await api.post(`${API_URL}/social/linkedin/callback`, {
            code,
            state,
            client_id: clientId,
            client_secret: clientSecret,
            redirect_uri: redirectUri,
            organization_id: organizationId
          });

          // Clean up localStorage
          localStorage.removeItem('linkedin_client_id');
          localStorage.removeItem('linkedin_client_secret');
          localStorage.removeItem('linkedin_organization_id');
          localStorage.removeItem('linkedin_redirect_uri');
          localStorage.removeItem('linkedin_oauth_state');

          toast.success(response.data?.message || 'LinkedIn connected successfully!');
          
        } else if (path.includes('instagram')) {
          setStatus('Connecting Instagram...');
          const appId = localStorage.getItem('instagram_app_id');
          const appSecret = localStorage.getItem('instagram_app_secret');
          const redirectUri = `${window.location.origin}/auth/instagram/callback`;

          if (!appId || !appSecret) {
            throw new Error('Instagram credentials not found. Please try connecting again.');
          }

          const response = await api.post(`${API_URL}/social/instagram/callback`, {
            code,
            state,
            client_id: appId,
            client_secret: appSecret,
            redirect_uri: localStorage.getItem('instagram_redirect_uri') || redirectUri
          });

          // Clean up localStorage
          localStorage.removeItem('instagram_app_id');
          localStorage.removeItem('instagram_app_secret');
          localStorage.removeItem('instagram_oauth_state');

          toast.success(response.data?.message || 'Instagram connected successfully!');
        }

        setStatus('Connected! Redirecting...');
        setTimeout(() => navigate('/automation/social'), 1500);

      } catch (err) {
        const errorMsg = err.response?.data?.detail || err.message || 'Connection failed';
        setStatus(`Error: ${errorMsg}`);
        toast.error(errorMsg);
        setTimeout(() => navigate('/automation/social'), 3000);
      }
    };

    handleCallback();
  }, [searchParams, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50" data-testid="oauth-callback-page">
      <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
        <h2 className="text-xl font-semibold text-gray-800 mb-2">Connecting Account</h2>
        <p className="text-gray-500" data-testid="oauth-status">{status}</p>
      </div>
    </div>
  );
}
